import type { Vec2 } from '../../geo/types';
import type { LifePose, LifeRoute } from './types';
import { lifePoseAt } from './routes';
import { distance } from './streetGraph';
import { bodyRadius, offsetAt, PACE, type Walker } from './castPace';

export const visible = (pose: LifePose): boolean => pose.visible !== false;

/**
 * What one of the cast makes of the others: who is in its way, who it must slow behind and who
 * it must step aside for. `out` tells whether a route's citizen is in the street at this hour;
 * one who is not is in no one's way.
 */
export function createCompany(
  routes: readonly LifeRoute[],
  walkers: ReadonlyMap<LifeRoute, Walker>,
  out: (route: LifeRoute) => boolean
) {
  const occupied = (route: LifeRoute, p: Vec2, before: Vec2): boolean => {
    for (const other of routes) {
      if (other === route || !out(other)) continue;
      const at = walkers.get(other)!.pose;
      if (!visible(at)) continue;
      const gap = bodyRadius(route) + bodyRadius(other) + 0.04;
      const nextDistance = distance(p, at.position);
      // Actors that begin close together can move apart, rather than locking each other in place.
      if (nextDistance < gap && nextDistance < distance(before, at.position) - 1e-6) return true;
    }
    return false;
  };
  /* Two who walk together are first put where their itineraries have them, and round a bend
     that is nearer than they keep. The later of them starts a moment behind. */
  routes.forEach((route, i) => {
    const walker = walkers.get(route)!;
    const crowded = (): boolean =>
      visible(walker.pose) &&
      routes.slice(0, i).some((other) => {
        const at = walkers.get(other)!.pose;
        return (
          visible(at) &&
          distance(walker.pose.position, at.position) < bodyRadius(route) + bodyRadius(other) + 0.04
        );
      });
    for (let back = 1; back <= 20 && crowded(); back++) {
      walker.clock = -0.1 * back;
      walker.pose = lifePoseAt(route, walker.clock);
    }
    if (crowded()) {
      walker.clock = 0;
      walker.pose = lifePoseAt(route, 0);
    }
  });
  /* Whether someone stands where a walker would have to pass. */
  const crowded = (route: LifeRoute, at: Vec2): boolean =>
    routes.some((other) => {
      if (other === route || !out(other)) return false;
      const theirs = walkers.get(other)!.pose;
      return (
        visible(theirs) &&
        distance(at, theirs.position) < bodyRadius(route) + bodyRadius(other) + 0.04
      );
    });
  const threatened = (route: LifeRoute, walker: Walker): boolean => {
    const future = lifePoseAt(route, walker.clock + 1.1);
    for (const other of routes) {
      if (other === route || !out(other)) continue;
      const next = walkers.get(other)!,
        at = next.pose;
      if (!visible(at) || distance(walker.pose.position, at.position) > 3.5) continue;
      /* Two going the same way are no threat to one another: the one behind keeps its distance
         (headway). Stepping out to pass someone a hand's breadth slower took the whole street. */
      if (
        walker.speed > PACE.crawl &&
        next.speed > PACE.crawl &&
        Math.cos(at.heading - walker.pose.heading) > 0.5
      )
        continue;
      const theirs = lifePoseAt(other, next.clock + (at.walking ? 1.1 : 0));
      const gap = bodyRadius(route) + bodyRadius(other) + 0.25;
      const apart = distance(walker.pose.position, at.position);
      const then = distance(
        offsetAt(future.position, walker.offset),
        offsetAt(theirs.position, next.offset)
      );
      if (then < gap) return true;
      // One who keeps its distance behind another, as a dog behind its owner, is in nobody's way.
      if (apart < gap + 0.3 && then < apart - 0.05) return true;
    }
    return false;
  };
  /* The pace at which a walker going `direction` can still stop short of whoever is before it,
     and whether the one it slows for is standing there. */
  const headway = (
    route: LifeRoute,
    from: Vec2,
    direction: number,
    braking: number
  ): { pace: number; standing: boolean } => {
    const [ux, uy] = [Math.cos(direction), Math.sin(direction)];
    const found = { pace: Infinity, standing: false };
    for (const other of routes) {
      if (other === route || !out(other)) continue;
      const theirs = walkers.get(other)!,
        at = theirs.pose;
      if (!visible(at)) continue;
      const [dx, dy] = [at.position[0] - from[0], at.position[1] - from[1]];
      const along = dx * ux + dy * uy;
      if (along <= 0 || along > PACE.headwayM) continue;
      const across = Math.abs(dy * ux - dx * uy);
      const gap = bodyRadius(route) + bodyRadius(other) + 0.04;
      if (across >= gap) continue;
      const free = along - Math.sqrt(gap * gap - across * across) - PACE.followM;
      const away = theirs.speed * Math.cos(at.heading - direction);
      // One coming the other way steps aside as this one does: slowing for it would stop both.
      if (away < -PACE.crawl) continue;
      const pace = Math.max(0, away) + Math.sqrt(2 * braking * Math.max(0, free));
      if (pace >= found.pace) continue;
      found.pace = pace;
      found.standing = theirs.speed < PACE.crawl;
    }
    return found;
  };
  return { occupied, crowded, threatened, headway };
}
